import React, { useState, useEffect, useMemo } from "react";
import useIncidentTypes from "../../../../../hooks/useIncidentTypes";
import { useFormik } from "formik";
import { GuestValidator, RegularUserValidator } from "../logIncident/validator";
import useIncident from "../../../../../hooks/useIncident";
import useUser from "../../../hooks/useUser";
import { useStoreWrapper } from "../../../../../store/store";
import Select from "react-select";
import { customStyles } from "../../../../../utils/helpers";
import CustomLoader from "../../../../../components/CustomLoader";
import _ from "lodash";
import {
  Container,
  Header,
  Incidents,
  Incident,
  IncidentLabel,
  HeaderContainer,
  CloseIcon,
  NextBtn,
} from "./incident.style";
import {
  Label,
  SelectContainer,
  LoadingIcon,
} from "../logIncident/logIncident.style";

const IncidentType = ({
  handleCloseModal,
  isGuest,
  handleIncidentLog,
}: any) => {
  const { incidentTypes, incidentTypesLoading } = useIncidentTypes();
  const [incidentList, setIncidentList] = useState([...incidentTypes]);
  const [isRefetchingResidents, setIsRefetchingResidents] = useState(false);

  useEffect(() => {
    setIncidentList([...incidentTypes]);
  }, [incidentTypesLoading, incidentTypes.length]);

  const selectIncident = (item: any) => {
    const incidentList__c = [...incidentList];
    incidentList__c.forEach((item) => (item.isActive = false));
    incidentList__c[incidentList.indexOf(item)].isActive = true;
    setIncidentList(incidentList__c);
  };

  const formik = useFormik({
    initialValues: {
      facilityId: "",
      residentId: "",
    },
    validationSchema: isGuest ? GuestValidator() : RegularUserValidator(),
    onSubmit: (values) => {
      handleIncidentLog({
        ...values,
        incidentType: incidentList.find((item) => item.isActive),
      });
    },
  });

  const { getResidentsInFacility } = useIncident({
    facilityId: formik.values["facilityId"],
  });

  const { handleGetUserFacilities } = useUser();

  const {
    isLoading: residentsLoading,
    data: residents,
    refetch,
  } = getResidentsInFacility;
  const { isLoading: facilitiesLoading, data: userFacilitiesData } =
    handleGetUserFacilities;

  const allUserFacilities = facilitiesLoading
    ? []
    : userFacilitiesData?.data?.data.facilities;

  const _allResidents =
    residentsLoading || !formik.values["facilityId"]
      ? []
      : residents?.data?.data;
  const allResidents = useMemo(
    () =>
      _allResidents.map((residentInFacility: any) => {
        return {
          ...residentInFacility,
          label: `${residentInFacility.firstName} ${residentInFacility.lastName}`,
          value: residentInFacility.userId,
        };
      }),
    [_allResidents, residents, formik.values["facilityId"]]
  );

  const userFacilities = useStoreWrapper((state) => state.userFacilities);

  const facilitiesWithCreatePermission = allUserFacilities.reduce(
    (acc: any, facility: any) => {
      const facilityWithPermissions = userFacilities.find(
        (element: any) => facility.facilityId === element.facilityId
      );
      if (facilityWithPermissions) {
        const permissions =
          facilityWithPermissions.permissions.incidentManagementPermissions;
        const createIncidentObj = permissions
          ? permissions.find(
              (el: { permissionLabel: string }) =>
                el.permissionLabel === "Create Incident"
            )
          : null;
        const canCreateIncident = createIncidentObj.permissionValue ?? false;
        if (canCreateIncident) acc.push(facility);
      }
      return acc;
    },
    []
  );

  const sortList = (list: any, sortKey: any) => {
    return _.sortBy(list, (o) => o[sortKey].toLowerCase());
  };

  useEffect(() => {
    if (formik.values["facilityId"]) {
      setIsRefetchingResidents(true);
      refetch().then(() => setIsRefetchingResidents(false));
    }
  }, [formik.values["facilityId"], refetch]);
  function getResidentLabel(value: any): string {
    return value.label;
  }

  function getFacilityLabel(value: any): string {
    return value.facilityName;
  }

  function getResidentValue(value: any): string {
    return value.value;
  }

  function getFacilityValue(value: any): string {
    return value.facilityId;
  }

  function getSelectedResidentOption(): string {
    // return
    let res = allResidents
      ? allResidents.find(
          (res: { value: string }) => res.value === formik.values["residentId"]
        ) || ""
      : "";
    return res;
  }

  function getSelectedFacilityOption(): string {
    let selectedFacilityOption = facilitiesWithCreatePermission
      ? facilitiesWithCreatePermission.find(
          (res: { facilityId: string }) =>
            res.facilityId === formik.values["facilityId"]
        )
      : "";
    return selectedFacilityOption;
  }

  function handleResidentChange(value: any) {
    formik.setFieldValue("residentId", value.value);
  }

  function handleFacilityChange(value: any) {
    formik.setFieldValue("facilityId", value.facilityId);
    formik.setFieldValue("residentId", "");
  }

  function handleBlur(name: string) {
    formik.handleBlur({ target: { name: name } });
  }
  // disable next button if no facility is selected or no resident is selected
  const disableNextBtn =
    !formik.values["facilityId"] && !formik.values["residentId"];

  const isResidentsLoading =
    !!formik.values["facilityId"] &&
    (residentsLoading || isRefetchingResidents);
  const isResidentsDisabled =
    !formik.values["facilityId"] || residentsLoading || isRefetchingResidents;

  return (
    <Container>
      <div className="block w-full md:hidden">
        <CloseIcon data-testid="close-icon" onClick={() => handleCloseModal()}>
          &times;
        </CloseIcon>
      </div>
      <HeaderContainer>
        <div className="hidden md:block">
          <CloseIcon
            data-testid="close-icon"
            onClick={() => handleCloseModal()}
          >
            &times;
          </CloseIcon>
        </div>
        <Header> Select Incident Type</Header>
        <div>
          <NextBtn
            type="submit"
            onClick={() => {
              formik.handleSubmit();
            }}
            disabled={!!disableNextBtn}
            data-testid="next-btn"
          >
            Next
          </NextBtn>
        </div>
      </HeaderContainer>

      <div className="mt-3" style={{ width: "100%" }}>
        <form>
          <div className="flex-row lg:flex">
            <div className="mb-4 flex-1 lg:mr-3">
              <Label>Select Facility *</Label>
              <Select
                name="facilityId"
                options={sortList(
                  facilitiesWithCreatePermission,
                  "facilityName"
                )}
                getOptionLabel={getFacilityLabel}
                getOptionValue={getFacilityValue}
                className="basic-multi-select"
                classNamePrefix="select"
                onBlur={() => handleBlur("facilityId")}
                styles={customStyles}
                onChange={handleFacilityChange}
                placeholder="Select Facility"
                value={getSelectedFacilityOption()}
              />

              {formik.touched.facilityId && formik.errors.facilityId && (
                <div className="text-sm text-red-500">
                  {formik.errors.facilityId}
                </div>
              )}
            </div>

            {!isGuest && (
              <div className="flex-1">
                <SelectContainer>
                  <Label>Resident*</Label>
                  <Select
                    name="residentId"
                    options={
                      isResidentsLoading ? [] : sortList(allResidents, "label")
                    }
                    getOptionLabel={getResidentLabel}
                    getOptionValue={getResidentValue}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onBlur={() => handleBlur("residentId")}
                    styles={customStyles}
                    isDisabled={isResidentsDisabled}
                    onChange={handleResidentChange}
                    placeholder="Select Residents"
                    value={getSelectedResidentOption()}
                  />
                  {isResidentsLoading ? (
                    <LoadingIcon>
                      <CustomLoader></CustomLoader>
                    </LoadingIcon>
                  ) : null}
                  {formik.touched.residentId && formik.errors.residentId && (
                    <div className="text-sm text-red-500">
                      {formik.errors.residentId}
                    </div>
                  )}
                </SelectContainer>
              </div>
            )}
          </div>
        </form>
      </div>

      {!incidentTypesLoading ? (
        <Incidents>
          {incidentList.map((item, i) => (
            //@ts-ignore
            <Incident
              key={item.id}
              onClick={() => selectIncident(item)}
              // @ts-ignore
              onDoubleClick={() => formik.handleSubmit()}
              isActive={item.isActive}
              data-testid={item.label}
            >
              {item.icon && (
                <item.icon
                  color={item.isActive ? "whitesmoke" : "#3D0052"}
                  size={60}
                />
              )}
              {/* @ts-ignore */}
              <IncidentLabel isActive={item.isActive}>
                {" "}
                {item.label}{" "}
              </IncidentLabel>
            </Incident>
          ))}
        </Incidents>
      ) : (
        <div>
          <CustomLoader fullscreen />
        </div>
      )}
    </Container>
  );
};

export default IncidentType;
